import type { Cliente, ClassificacaoCliente } from '@pgcustom/shared-types';
import { formatarCpfExibicao } from '@pgcustom/shared-utils';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { ClientesApi, AtualizarClienteInput } from './clientes.api.js';
import type { PermissoesClientes } from './clientes.permissoes.js';
import { CLASSIFICACOES_CLIENTE } from './classificacao.js';
import { construirPayloadCriarCliente } from './cliente-form.util.js';

type Modo = 'criar' | 'editar';

/**
 * `<erp-cliente-form-modal>` — modal de criação/edição de cliente, um
 * único componente para os dois modos (mesma decisão de não duplicar
 * estrutura visual entre casos de uso próximos já usada no resto do
 * frontend). Dependências (`clientesApi`, `permissoes`, callback de
 * sucesso) injetadas via propriedade JS — mesmo padrão de
 * `erp-login-page`.
 *
 * Quando `permissoes.atualizar` é falso, abre em modo somente-leitura
 * (financeiro/suporte/marketing podem ver o cadastro, nunca alterá-lo —
 * ADR 011). Exclusão NUNCA é feita por este componente: ele apenas
 * emite `erp-cliente-excluir-solicitado` e se fecha, deixando a
 * confirmação e a chamada real a cargo de `erp-clientes-page` — evita
 * dois `pg-modal` (confirmação + formulário) abertos ao mesmo tempo.
 */
export class ErpClienteFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _clientesApi: ClientesApi | null = null;
  private _permissoes: PermissoesClientes = { ler: false, criar: false, atualizar: false, excluir: false };
  private _aoSalvar: ((cliente: Cliente) => void) | null = null;

  private aberto = false;
  private modo: Modo = 'criar';
  private clienteAtual: Cliente | null = null;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set clientesApi(valor: ClientesApi) {
    this._clientesApi = valor;
  }

  set permissoes(valor: PermissoesClientes) {
    this._permissoes = valor;
  }

  set aoSalvar(valor: (cliente: Cliente) => void) {
    this._aoSalvar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrirParaCriar(): void {
    this.modo = 'criar';
    this.clienteAtual = null;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  abrirParaEditar(cliente: Cliente): void {
    this.modo = 'editar';
    this.clienteAtual = cliente;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private solicitarExclusao(): void {
    if (!this.clienteAtual) return;
    const cliente = this.clienteAtual;
    this.aberto = false;
    this.render();
    this.dispatchEvent(
      new CustomEvent('erp-cliente-excluir-solicitado', { bubbles: true, composed: true, detail: { cliente } })
    );
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._clientesApi || this.enviando || this.somenteLeitura()) return;

    const nome = this.valorCampo('#nome').trim();
    const email = this.valorCampo('#email').trim();
    const cpf = this.valorCampo('#cpf').trim();
    const telefone = this.valorCampo('#telefone').trim();
    const classificacao = this.valorCampo('#classificacao') as ClassificacaoCliente;

    if (!nome) {
      this.mensagemErro = 'nome é obrigatório';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      let cliente: Cliente;
      if (this.modo === 'criar') {
        const dado = construirPayloadCriarCliente(nome, email, cpf, telefone);
        cliente = await this._clientesApi.criar(dado);
      } else {
        if (!this.clienteAtual) throw new Error('cliente ausente para edição');
        const patch: AtualizarClienteInput = {
          nome,
          email: email || null,
          telefone: telefone || null,
          classificacao,
        };
        // CPF nunca é pré-preenchido com o valor mascarado recebido da
        // API (ADR 012) — campo vazio aqui sempre significa "sem
        // alteração", só é enviado quando o usuário digita um valor novo.
        if (cpf) patch.cpf = cpf;
        cliente = await this._clientesApi.atualizar(this.clienteAtual.id, patch);
      }
      this.aberto = false;
      this._aoSalvar?.(cliente);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private somenteLeitura(): boolean {
    return this.modo === 'editar' && !this._permissoes.atualizar;
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLSelectElement>(seletor)?.value ?? '';
  }

  private render(): void {
    const modo = this.modo;
    const cliente = this.clienteAtual;
    const somenteLeitura = this.somenteLeitura();
    const mostrarExcluir = modo === 'editar' && this._permissoes.excluir;
    const titulo = modo === 'criar' ? 'Novo cliente' : somenteLeitura ? 'Cliente' : 'Editar cliente';

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
        .rodape-entre { display: flex; justify-content: space-between; align-items: center; gap: var(--pg-space-3, 12px); width: 100%; }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">${escaparHtml(titulo)}</span>
        <form novalidate>
          <label>
            Nome
            <input id="nome" type="text" required maxlength="255" value="${escaparHtml(cliente?.nome ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          <label>
            E-mail
            <input id="email" type="email" maxlength="255" value="${escaparHtml(cliente?.email ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          <label>
            CPF
            <input id="cpf" type="text" maxlength="20" placeholder="${modo === 'editar' ? escaparHtml(cliente?.cpf ? formatarCpfExibicao(cliente.cpf) : 'sem CPF cadastrado') : 'somente números ou com pontuação'}" ${somenteLeitura ? 'disabled' : ''} />
            ${modo === 'editar' && !somenteLeitura ? '<span class="dica">deixe em branco para manter o CPF atual</span>' : ''}
          </label>
          <label>
            Telefone
            <input id="telefone" type="text" maxlength="30" value="${escaparHtml(cliente?.telefone ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          <label>
            Classificação
            <select id="classificacao" ${somenteLeitura || modo === 'criar' ? 'disabled' : ''}>
              ${CLASSIFICACOES_CLIENTE.map(
                (opcao) =>
                  `<option value="${opcao.valor}" ${(cliente?.classificacao ?? 'padrao') === opcao.valor ? 'selected' : ''}>${opcao.rotulo}</option>`
              ).join('')}
            </select>
            ${modo === 'criar' ? '<span class="dica">todo cliente novo começa como Padrão</span>' : ''}
          </label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape-entre">
          <div>
            ${mostrarExcluir ? '<pg-button id="botao-excluir" variant="danger">Excluir cliente</pg-button>' : ''}
          </div>
          <div class="rodape">
            <pg-button id="botao-cancelar" variant="ghost">${somenteLeitura ? 'Fechar' : 'Cancelar'}</pg-button>
            ${!somenteLeitura ? `<pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Salvar</pg-button>` : ''}
          </div>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#botao-excluir')?.addEventListener('pg-click', () => this.solicitarExclusao());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-cliente-form-modal', ErpClienteFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-cliente-form-modal': ErpClienteFormModal;
  }
}
